/**
 * Local address pre-processing pipeline.
 * Handles deterministic fixes (phone formatting, whitespace, street parsing,
 * addressExtra cleanup, region expansion) so the AI is only called for
 * complex cases like non-Latin transliteration or ambiguous field redistribution.
 */

// ── Phone normalization ─────────────────────────────────────────────────────

const CALLING_CODES: Record<string, string> = {
  NL: '31', BE: '32', DE: '49', FR: '33', IT: '39', ES: '34',
  PL: '48', PT: '351', AT: '43', CH: '41', DK: '45', SE: '46',
  NO: '47', FI: '358', IE: '353', GB: '44', US: '1', CA: '1',
  AU: '61', JP: '81', KR: '82', BR: '55', MX: '52', IN: '91',
  SA: '966', AE: '971', IL: '972', TR: '90', ZA: '27', NZ: '64',
  CZ: '420', SK: '421', HU: '36', RO: '40', BG: '359', HR: '385',
  SI: '386', RS: '381', GR: '30', CY: '357', LU: '352', LT: '370',
  LV: '371', EE: '372', IS: '354', MT: '356', AL: '355', BA: '387',
  ME: '382', MK: '389', XK: '383', MD: '373', UA: '380', BY: '375',
  RU: '7', CN: '86', TH: '66', MY: '60', SG: '65', ID: '62',
  PH: '63', VN: '84', TW: '886', HK: '852', AR: '54', CO: '57',
  CL: '56', PE: '51', VE: '58', EC: '593', UY: '598', PY: '595',
  BO: '591', CR: '506', PA: '507', DO: '1', GT: '502', HN: '504',
  SV: '503', NI: '505', CU: '53', JM: '1', TT: '1', EG: '20',
  MA: '212', TN: '216', DZ: '213', NG: '234', KE: '254', GH: '233',
  TZ: '255', UG: '256', ET: '251', CI: '225', SN: '221',
};

const TRUNK_PREFIX_COUNTRIES = new Set([
  'NL', 'BE', 'DE', 'FR', 'IT', 'ES', 'PL', 'PT', 'AT', 'GB', 'IE',
  'SE', 'NO', 'FI', 'DK', 'CH', 'HU', 'RO', 'BG', 'HR', 'CZ', 'SK',
  'SI', 'RS', 'GR', 'CY', 'TR', 'ZA', 'NZ', 'AU', 'JP', 'ID', 'MY',
  'TH', 'VN', 'PH', 'KE', 'NG', 'EG', 'MA', 'AR', 'BR', 'MX', 'CO',
]);

export function normalizePhone(phone: string | undefined, countryCode: string): string {
  if (!phone) return '';
  const trimmed = phone.trim();
  if (!trimmed || trimmed.startsWith('+')) return trimmed;

  const cc = countryCode.toUpperCase();
  const callingCode = CALLING_CODES[cc];
  if (!callingCode) return trimmed;

  const digits = trimmed.replace(/[\s\-().]/g, '');
  if (!digits || !/^\d+$/.test(digits)) return trimmed;

  // Dropped leading 00 + CC (same as "+" international format typed without +), e.g. 00351914… → PT national 914…
  const intlPrefix = `00${callingCode}`;
  let national = digits;
  if (
    digits.startsWith(intlPrefix)
    && digits.length > intlPrefix.length + 5
  ) {
    national = digits.slice(intlPrefix.length);
  }

  const cleaned = TRUNK_PREFIX_COUNTRIES.has(cc) && national.startsWith('0')
    ? national.slice(1)
    : national;

  return `+${callingCode} ${cleaned}`;
}

// ── Street field cleanup ────────────────────────────────────────────────────

const COUNTRY_NAMES: Record<string, string[]> = {
  NL: ['nederland', 'netherlands', 'holland', 'the netherlands'],
  BE: ['belgie', 'belgique', 'belgium'],
  DE: ['deutschland', 'germany', 'allemagne'],
  FR: ['france', 'frankrijk'],
  IT: ['italia', 'italy', 'italie'],
  ES: ['espana', 'españa', 'spain', 'spanje'],
  PL: ['polska', 'poland', 'polen'],
  PT: ['portugal'],
  AT: ['osterreich', 'österreich', 'austria'],
  CH: ['schweiz', 'suisse', 'svizzera', 'switzerland'],
  DK: ['danmark', 'denmark', 'denemarken'],
  SE: ['sverige', 'sweden', 'zweden'],
  NO: ['norge', 'norway', 'noorwegen'],
  FI: ['suomi', 'finland'],
  IE: ['ireland', 'ierland'],
  GB: ['united kingdom', 'uk', 'great britain', 'england'],
  US: ['united states', 'usa', 'us', 'united states of america'],
  CA: ['canada'],
  AU: ['australia'],
  JP: ['japan', 'nippon'],
  GR: ['greece', 'hellas', 'ελλάδα'],
  CZ: ['czech republic', 'czechia', 'cesko'],
  HR: ['croatia', 'hrvatska'],
  RO: ['romania', 'românia'],
  HU: ['hungary', 'magyarorszag'],
  BG: ['bulgaria'],
  TR: ['turkey', 'türkiye', 'turkiye'],
};

export function cleanStreetField(
  street: string | undefined,
  city: string | undefined,
  region: string | undefined,
  countryCode: string | undefined,
): string {
  if (!street) return '';
  let s = street.trim().replace(/\s{2,}/g, ' ');

  if (!s.includes(',')) return s;

  const parts = s.split(',').map(p => p.trim());
  if (parts.length < 2) return s;

  const cityLower = (city || '').toLowerCase().trim();
  const regionLower = (region || '').toLowerCase().trim();
  const cc = (countryCode || '').toUpperCase();
  const countryNames = new Set<string>();
  if (cc) {
    countryNames.add(cc.toLowerCase());
    for (const name of (COUNTRY_NAMES[cc] || [])) countryNames.add(name);
  }

  let trimEnd = parts.length;
  for (let i = parts.length - 1; i >= 1; i--) {
    const partLower = parts[i].toLowerCase();
    if (!partLower) { trimEnd = i; continue; }
    const isCity = cityLower && partLower === cityLower;
    const isRegion = regionLower && (partLower === regionLower);
    const isCountry = countryNames.has(partLower);
    if (isCity || isRegion || isCountry) {
      trimEnd = i;
    } else {
      break;
    }
  }

  if (trimEnd < parts.length) {
    s = parts.slice(0, trimEnd).join(', ');
  }

  return s;
}

// ── House number extraction (ported from server-side streetHouseParser.ts) ──

interface ExtractedHouseNumber {
  street: string;
  houseNumber: string;
  houseNumberExt?: string;
}

export function extractHouseNumber(
  street: string | undefined,
  existingHouseNumber: string | undefined,
): ExtractedHouseNumber {
  const s = (street || '').trim();
  const existing = (existingHouseNumber || '').trim();

  if (!s) return { street: '', houseNumber: existing };
  if (existing) return { street: s, houseNumber: existing };

  // PT/FR-style: "… Avenida … n°62" — number after n°/nº
  const ndeg = s.match(/^(.+?)\s+n[°º]\s*(\d+)([A-Za-z]?)\s*$/iu);
  if (ndeg) {
    return {
      street: ndeg[1].trim(),
      houseNumber: ndeg[2].trim(),
      ...(ndeg[3] ? { houseNumberExt: ndeg[3].trim() } : {}),
    };
  }

  // Comma-separated with slash: "Street, 3/A"
  const commaSlash = s.match(/^(.+?),\s*(\d+)\s*([/\-])\s*(\d*[A-Za-z]?)\s*$/);
  if (commaSlash) {
    return {
      street: commaSlash[1].trim(),
      houseNumber: commaSlash[2].trim(),
      houseNumberExt: commaSlash[4]?.trim() ? `${commaSlash[3]}${commaSlash[4]}` : undefined,
    };
  }

  // Comma-separated: "Street, 12A"
  const comma = s.match(/^(.+?),\s*(\d+)\s*([A-Za-z]?\d*[A-Za-z]*)?\s*$/);
  if (comma) {
    return {
      street: comma[1].trim(),
      houseNumber: comma[2].trim(),
      houseNumberExt: comma[3]?.trim() || undefined,
    };
  }

  // Trailing number with slash: "Street 3/A"
  const trailingSlash = s.match(/^(.+?)\s+(\d+)\s*([/\-])\s*([A-Za-z0-9]+)\s*$/);
  if (trailingSlash) {
    return {
      street: trailingSlash[1].trim(),
      houseNumber: trailingSlash[2].trim(),
      houseNumberExt: trailingSlash[4].trim(),
    };
  }

  // Trailing number with letter ext: "Street 45b"
  const trailing = s.match(/^(.+?)\s+(\d+)\s*([A-Za-z]?\d*[A-Za-z]*)?\s*$/);
  if (trailing) {
    return {
      street: trailing[1].trim(),
      houseNumber: trailing[2].trim(),
      houseNumberExt: trailing[3]?.trim() || undefined,
    };
  }

  return { street: s, houseNumber: '' };
}

// ── addressExtra cleanup ────────────────────────────────────────────────────

export function cleanAddressExtra(
  addressExtra: string | undefined,
  houseNumber: string | undefined,
): string {
  if (!addressExtra) return '';
  const extra = addressExtra.trim();
  const hn = (houseNumber || '').trim();
  if (!extra) return extra;

  if (hn && extra === hn) return '';

  if (!hn) return extra;

  // "118/1" with houseNumber="118" -> "1"
  if (extra.startsWith(`${hn}/`)) {
    return extra.slice(hn.length + 1).trim();
  }

  return extra;
}

/** When addressExtra only repeats street + housenumber already in structured fields (e.g. Shopify paste). */
export function dedupeRedundantAddressExtra(
  street: string | undefined,
  houseNumber: string | undefined,
  houseNumberExt: string | undefined,
  addressExtra: string | undefined,
): string {
  const extra = (addressExtra || '').trim();
  if (!extra) return '';

  const st = (street || '').trim();
  const hn = (houseNumber || '').trim();
  const hext = (houseNumberExt || '').trim();

  const norm = (s: string): string =>
    s.toLowerCase().replace(/\s+/g, ' ').trim();

  const nEx = norm(extra);

  if (st && hn && nEx === norm(`${st} ${hn}`)) return '';
  if (st && hn && hext && nEx === norm(`${st} ${hn} ${hext}`)) return '';
  if (st && hn && hext && nEx === norm(`${st} ${hn}${hext}`)) return '';
  return extra;
}

// ── Region expansion (IT provinces for frontend) ────────────────────────────

const IT_PROVINCES: Record<string, string> = {
  AG: 'Agrigento', AL: 'Alessandria', AN: 'Ancona', AO: 'Aosta',
  AR: 'Arezzo', AP: 'Ascoli Piceno', AT: 'Asti', AV: 'Avellino',
  BA: 'Bari', BT: 'Barletta-Andria-Trani', BL: 'Belluno', BN: 'Benevento',
  BG: 'Bergamo', BI: 'Biella', BO: 'Bologna', BZ: 'Bolzano',
  BS: 'Brescia', BR: 'Brindisi', CA: 'Cagliari', CL: 'Caltanissetta',
  CB: 'Campobasso', CE: 'Caserta', CT: 'Catania', CZ: 'Catanzaro',
  CH: 'Chieti', CO: 'Como', CS: 'Cosenza', CR: 'Cremona',
  KR: 'Crotone', CN: 'Cuneo', EN: 'Enna', FM: 'Fermo',
  FE: 'Ferrara', FI: 'Firenze', FG: 'Foggia', FC: 'Forlì-Cesena',
  FR: 'Frosinone', GE: 'Genova', GO: 'Gorizia', GR: 'Grosseto',
  IM: 'Imperia', IS: 'Isernia', SP: 'La Spezia', AQ: "L'Aquila",
  LT: 'Latina', LE: 'Lecce', LC: 'Lecco', LI: 'Livorno',
  LO: 'Lodi', LU: 'Lucca', MC: 'Macerata', MN: 'Mantova',
  MS: 'Massa-Carrara', MT: 'Matera', ME: 'Messina', MI: 'Milano',
  MO: 'Modena', MB: 'Monza e Brianza', NA: 'Napoli', NO: 'Novara',
  NU: 'Nuoro', OR: 'Oristano', PD: 'Padova', PA: 'Palermo',
  PR: 'Parma', PV: 'Pavia', PG: 'Perugia', PU: 'Pesaro e Urbino',
  PE: 'Pescara', PC: 'Piacenza', PI: 'Pisa', PT: 'Pistoia',
  PN: 'Pordenone', PZ: 'Potenza', PO: 'Prato', RG: 'Ragusa',
  RA: 'Ravenna', RC: 'Reggio Calabria', RE: 'Reggio Emilia', RI: 'Rieti',
  RN: 'Rimini', RM: 'Roma', RO: 'Rovigo', SA: 'Salerno',
  SS: 'Sassari', SV: 'Savona', SI: 'Siena', SR: 'Siracusa',
  SO: 'Sondrio', SU: 'Sud Sardegna', TA: 'Taranto', TE: 'Teramo',
  TR: 'Terni', TO: 'Torino', TP: 'Trapani', TN: 'Trento',
  TV: 'Treviso', TS: 'Trieste', UD: 'Udine', VA: 'Varese',
  VE: 'Venezia', VB: 'Verbano-Cusio-Ossola', VC: 'Vercelli', VR: 'Verona',
  VV: 'Vibo Valentia', VI: 'Vicenza', VT: 'Viterbo',
};

/** Mirrors supabase/functions/_shared/region.ts GB/PT rules forShipping Labels UI parity */
const GB_HOME_NATIONS: Record<string, string> = {
  ENG: 'England',
  SCT: 'Scotland',
  WLS: 'Wales',
  NIR: 'Northern Ireland',
};

const PT_DISTRITOS_UI: Record<string, string> = {
  '01': 'Aveiro',
  '02': 'Beja',
  '03': 'Braga',
  '04': 'Bragança',
  '05': 'Castelo Branco',
  '06': 'Coimbra',
  '07': 'Évora',
  '08': 'Faro',
  '09': 'Guarda',
  '10': 'Leiria',
  '11': 'Lisboa',
  '12': 'Portalegre',
  '13': 'Porto',
  '14': 'Santarém',
  '15': 'Setúbal',
  '16': 'Viana do Castelo',
  '17': 'Vila Real',
  '18': 'Viseu',
  '20': 'Azores',
  '30': 'Madeira',
};

export function expandRegion(region: string | undefined, countryCode: string | undefined): string {
  if (!region) return '';
  const r = region.trim();
  if (!r) return '';
  const cc = (countryCode || '').toUpperCase();

  if (cc === 'IT') {
    const upper = r.toUpperCase();
    if (IT_PROVINCES[upper]) return IT_PROVINCES[upper];
  }

  if (cc === 'GB') {
    const upper = r.toUpperCase();
    if (GB_HOME_NATIONS[upper]) return GB_HOME_NATIONS[upper];
  }

  if (cc === 'PT') {
    const m = r.toUpperCase().match(/^PT[- ]?(\d{2})$/);
    if (m && PT_DISTRITOS_UI[m[1]]) return PT_DISTRITOS_UI[m[1]];
  }

  return r;
}

// ── Orchestrator ────────────────────────────────────────────────────────────

export interface PreprocessedAddress {
  name: string;
  street: string;
  houseNumber: string;
  houseNumberExt: string;
  addressExtra: string;
  postalCode: string;
  city: string;
  countryCode: string;
  phone: string;
  region: string;
}

export interface PreprocessResult {
  address: PreprocessedAddress;
  changes: Array<{ field: string; message: string }>;
  /** True when only trivial/deterministic fields changed (phone, whitespace, addressExtra dup) */
  trivialOnly: boolean;
}

export function preprocessAddress(input: {
  name?: string;
  street?: string;
  houseNumber?: string;
  houseNumberExt?: string;
  addressExtra?: string;
  postalCode?: string;
  city?: string;
  countryCode?: string;
  phone?: string;
  region?: string;
}): PreprocessResult {
  const changes: Array<{ field: string; message: string }> = [];
  const cc = (input.countryCode || '').toUpperCase();
  let hasComplexChange = false;

  // 1. Normalize whitespace in street
  let street = (input.street || '').trim().replace(/\s{2,}/g, ' ');
  if (street !== (input.street || '').trim() && street) {
    changes.push({ field: 'street', message: 'Normalized whitespace in street' });
  }

  // 2. Strip trailing city/region/country from street
  const cleanedStreet = cleanStreetField(street, input.city, input.region, cc);
  if (cleanedStreet !== street) {
    changes.push({ field: 'street', message: 'Removed duplicate city/region/country from street' });
    street = cleanedStreet;
    hasComplexChange = true;
  }

  // 3. Extract house number from street if missing
  let houseNumber = (input.houseNumber || '').trim();
  let houseNumberExt = (input.houseNumberExt || '').trim();
  const extracted = extractHouseNumber(street, houseNumber);
  if (extracted.houseNumber && !houseNumber) {
    changes.push({ field: 'houseNumber', message: `Extracted house number "${extracted.houseNumber}" from street` });
    street = extracted.street;
    houseNumber = extracted.houseNumber;
    houseNumberExt = extracted.houseNumberExt || houseNumberExt;
    hasComplexChange = true;
  }

  // 4. Clean addressExtra
  const origExtraTrim = (input.addressExtra || '').trim();
  let addressExtra = cleanAddressExtra(input.addressExtra, houseNumber);
  addressExtra = dedupeRedundantAddressExtra(street, houseNumber, houseNumberExt, addressExtra);
  if (addressExtra !== origExtraTrim) {
    changes.push({
      field: 'addressExtra',
      message: 'Cleaned addressExtra (removed duplicate house segment or repeated street line)',
    });
    hasComplexChange = true;
  }

  // 5. Expand region codes
  const region = expandRegion(input.region, cc);
  if (region !== (input.region || '').trim() && region) {
    changes.push({ field: 'region', message: `Expanded region code to "${region}"` });
    hasComplexChange = true;
  }

  // 6. Format phone
  const phone = normalizePhone(input.phone, cc);
  if (phone !== (input.phone || '').trim() && phone) {
    changes.push({ field: 'phone', message: 'Formatted phone with international calling code' });
  }

  const address: PreprocessedAddress = {
    name: (input.name || '').trim(),
    street,
    houseNumber,
    houseNumberExt,
    addressExtra,
    postalCode: (input.postalCode || '').trim(),
    city: (input.city || '').trim(),
    countryCode: cc,
    phone,
    region,
  };

  return {
    address,
    changes,
    trivialOnly: !hasComplexChange,
  };
}
